import { useState } from 'react';
import type { StressResult, StressScenario } from '@prm/shared';
import { num, pct, usd } from '../format';
import { Panel } from './Panel';
import { FlagList } from './FlagList';

export function StressCard({ stress }: { stress: StressResult }) {
  const [open, setOpen] = useState<string | null>(null);

  const applicable = stress.scenarios.filter((s) => s.applicable);
  // Bars are scaled to the worst loss or the tolerance line, whichever is
  // larger, so the tolerance marker is always on the track.
  const scale = Math.max(
    ...applicable.map((s) => Math.abs(s.pctOfEquity)),
    stress.maxDrawdown,
    0.01,
  );

  return (
    <>
      <Panel
        title="Stress Tests"
        sub={`tolerance ${pct(stress.maxDrawdown, 0)} · ${applicable.length} scenarios`}
        flush
      >
        <div className="panel__body" style={{ paddingBottom: 6 }}>
          <div className="kv">
            <Stat
              k="Worst case"
              v={stress.worst === null ? '—' : pct(stress.worst.pctOfEquity)}
              note={stress.worst?.label ?? 'no scenarios evaluated'}
              bad={stress.worst !== null && stress.worst.pctOfEquity < -stress.maxDrawdown}
            />
            <Stat k="Tolerance" v={pct(stress.maxDrawdown, 0)} note="max tolerable drawdown" />
            <Stat
              k="Diversification"
              v={stress.diversificationRatio === null ? '—' : `${num(stress.diversificationRatio)}×`}
              note="undiversified vol ÷ realized"
              bad={stress.diversificationRatio !== null && stress.diversificationRatio < 1.15}
            />
          </div>
        </div>

        <table className="table">
          <thead>
            <tr>
              <th>Scenario</th>
              <th style={{ width: '38%' }}>Drawdown</th>
              <th style={{ width: 92 }}>%</th>
              <th style={{ width: 110 }}>P&amp;L</th>
              <th style={{ width: 28 }} />
            </tr>
          </thead>
          <tbody>
            {stress.scenarios.map((s) => (
              <ScenarioRow
                key={s.id}
                scenario={s}
                scale={scale}
                tolerance={stress.maxDrawdown}
                expanded={open === s.id}
                onToggle={() => setOpen(open === s.id ? null : s.id)}
              />
            ))}
          </tbody>
        </table>

        <div className="footnote">
          Scenario P&amp;L is modelled from Blume-adjusted betas and measured correlations, not forecast. Expand a row
          for the exact method.
        </div>
      </Panel>

      <Panel title="Stress flags" sub={`${stress.flags.length}`} flush>
        <FlagList flags={stress.flags} empty="No scenario breaches the drawdown tolerance." />
      </Panel>
    </>
  );
}

function ScenarioRow({
  scenario,
  scale,
  tolerance,
  expanded,
  onToggle,
}: {
  scenario: StressScenario;
  scale: number;
  tolerance: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const loss = Math.abs(scenario.pctOfEquity);
  const breach = scenario.breachesTolerance;

  return (
    <>
      <tr className={expanded ? 'is-open' : ''} onClick={onToggle} style={{ cursor: 'pointer' }}>
        <td style={{ textAlign: 'left' }}>
          {scenario.label}
          {!scenario.applicable ? <span className="chip chip--muted">n/a</span> : null}
        </td>
        <td>
          {scenario.applicable ? (
            <div className="bar__track" style={{ height: 10 }}>
              <div
                className={`bar__fill ${breach ? 'bar__fill--over' : ''}`}
                style={{ width: `${Math.min(100, (loss / scale) * 100)}%`, background: breach ? undefined : 'var(--warn)' }}
              />
              <div className="bar__limit" style={{ left: `${Math.min(100, (tolerance / scale) * 100)}%` }} />
            </div>
          ) : (
            <span className="faint" style={{ fontSize: 10 }}>
              not evaluated
            </span>
          )}
        </td>
        <td className={`num ${breach ? 'down' : ''}`}>{scenario.applicable ? pct(scenario.pctOfEquity) : '—'}</td>
        <td className={`num ${breach ? 'down' : ''}`}>{scenario.applicable ? usd(scenario.pnl) : '—'}</td>
        <td className="faint">{expanded ? '▾' : '▸'}</td>
      </tr>

      {expanded ? (
        <tr className="detail">
          <td colSpan={5}>
            <div className="detail__body">
              <p className="detail__text">{scenario.description}</p>
              {scenario.applicable ? (
                <>
                  <div className="detail__label">Method</div>
                  <p className="detail__text faint">{scenario.method}</p>
                  {scenario.contributors.length > 0 ? (
                    <>
                      <div className="detail__label">Largest contributors</div>
                      <div className="contributors">
                        {scenario.contributors.slice(0, 5).map((c) => (
                          <div className="contributor" key={c.ticker}>
                            <span className="ticker">{c.ticker}</span>
                            <span className={`num ${c.pnl < 0 ? 'down' : ''}`}>{usd(c.pnl)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </>
              ) : (
                <p className="detail__text faint">{scenario.notApplicableReason}</p>
              )}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Stat({ k, v, note, bad }: { k: string; v: string; note?: string; bad?: boolean }) {
  return (
    <div className="kv__item">
      <div className="kv__k">{k}</div>
      <div className={`kv__v num ${bad ? 'down' : ''}`}>{v}</div>
      {note ? <div className="faint" style={{ fontSize: 10 }}>{note}</div> : null}
    </div>
  );
}
